/**
 * Pipeline policy settings helpers (Sprint 278 — ROUTE-PIPE-1/A, REQ-LLM-006 §8.1).
 *
 * Conversión entre la forma plana de la API de política DR/tenant y los
 * bloques anidados `settings.{routing,optimization,loop,autonomy,timeouts}`
 * de hive/agente (misma forma que consume el resolver del backend).
 */

import type {
  AsyncServiceTier,
  FrontierOnUnavailable,
  GroundingCheck,
  OutputEffort,
  PipelinePolicyFields,
  PlanReview,
  PlanningOnFailure,
  PromptCachePartition,
  PromptCacheTtl,
  RoutingMode,
  RoutingOnFailure,
  ToolExposure,
} from '../types/policy';

export type SettingsDict = Record<string, unknown>;

function asDict(value: unknown): SettingsDict {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as SettingsDict)
    : {};
}

function firstDefined(...values: unknown[]): unknown {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

/** Flatten ``settings.{routing,optimization,loop,autonomy,timeouts}``. */
export function settingsToPolicyFields(
  settings?: SettingsDict | null
): PipelinePolicyFields {
  const s = settings ?? {};
  const routing = asDict(s.routing);
  const optimization = asDict(s.optimization);
  const cache = asDict(s.cache);
  const execution = asDict(s.execution);
  const loop = asDict(s.loop);
  const safety = asDict(s.safety);
  const autonomy = asDict(s.autonomy);
  const timeouts = asDict(s.timeouts);
  const pick = <T,>(...values: unknown[]): T | undefined =>
    firstDefined(...values) as T | undefined;

  return {
    routing_enabled: pick<boolean>(routing.enabled),
    routing_mode: pick<RoutingMode>(routing.mode),
    routing_on_failure: pick<RoutingOnFailure>(routing.on_failure),
    routing_confidence_threshold: pick<number>(routing.confidence_threshold),
    routing_timeout_ms: pick<number>(routing.timeout_ms),
    routing_budget_tokens: pick<number>(routing.budget_tokens),
    planning_on_failure: pick<PlanningOnFailure>(routing.planning_on_failure),
    grounding_check: pick<GroundingCheck>(routing.grounding_check),
    plan_review: pick<PlanReview>(routing.plan_review),
    retrieval_gating_enabled: pick<boolean>(
      routing.retrieval_gating_enabled
    ),
    optimization_enabled: pick<boolean>(optimization.enabled),
    // Sprint 280 (CHAT-TOK-1/B — REQ-LLM-006 rev.): claves con
    // representación en hive/agente (el resto no tiene mapping aquí).
    measurement_enabled: pick<boolean>(optimization.measurement_enabled),
    compare_tolerance_pct: pick<number>(optimization.compare_tolerance_pct),
    optimization_holdout_pct: pick<number>(optimization.holdout_pct),
    tool_exposure: pick<ToolExposure>(optimization.tool_exposure),
    tool_deferred_threshold_tokens: pick<number>(
      optimization.tool_deferred_threshold_tokens
    ),
    compaction_trigger_pct: pick<number>(optimization.compaction_trigger_pct),
    compaction_target_pct: pick<number>(optimization.compaction_target_pct),
    compaction_on_cold_cache: pick<boolean>(
      optimization.compaction_on_cold_cache
    ),
    prompt_cache_enabled: pick<boolean>(cache.enabled),
    prompt_cache_ttl: pick<PromptCacheTtl>(cache.ttl),
    prompt_cache_partition: pick<PromptCachePartition>(cache.partition),
    output_effort: pick<OutputEffort>(execution.output_effort),
    output_effort_dynamic: pick<boolean>(execution.output_effort_dynamic),
    async_service_tier: pick<AsyncServiceTier>(execution.async_service_tier),
    max_loop_iterations: pick<number>(
      loop.max_iterations,
      safety.max_loop_iterations
    ),
    frontier_agents_enabled: pick<boolean>(autonomy.frontier),
    frontier_max_duration_minutes: pick<number>(autonomy.max_duration_minutes),
    frontier_on_unavailable: pick<FrontierOnUnavailable>(
      autonomy.on_unavailable
    ),
    foreground_timeout_seconds: pick<number>(timeouts.foreground_seconds),
    delegation_sync_timeout_seconds: pick<number>(
      timeouts.delegation_sync_seconds
    ),
    delegation_background_timeout_seconds: pick<number>(
      timeouts.delegation_background_seconds
    ),
    fanout_aggregation_timeout_seconds: pick<number>(
      timeouts.fanout_aggregation_seconds
    ),
  };
}

function compact(block: SettingsDict): SettingsDict | undefined {
  const entries = Object.entries(block).filter(
    ([, v]) => v !== undefined && v !== null
  );
  return entries.length ? Object.fromEntries(entries) : undefined;
}

/**
 * Rebuild the managed settings blocks from flat fields.
 * ``null`` retira el override (vuelve a heredar); el resto de claves de
 * ``base`` (runner_policy, delegation, …) se preservan.
 */
export function policyFieldsToSettings(
  fields: PipelinePolicyFields,
  base?: SettingsDict | null
): SettingsDict {
  const next: SettingsDict = { ...(base ?? {}) };
  const blocks: Array<[string, SettingsDict | undefined]> = [
    [
      'routing',
      compact({
        enabled: fields.routing_enabled,
        mode: fields.routing_mode,
        on_failure: fields.routing_on_failure,
        confidence_threshold: fields.routing_confidence_threshold,
        timeout_ms: fields.routing_timeout_ms,
        budget_tokens: fields.routing_budget_tokens,
        planning_on_failure: fields.planning_on_failure,
        grounding_check: fields.grounding_check,
        plan_review: fields.plan_review,
        retrieval_gating_enabled: fields.retrieval_gating_enabled,
      }),
    ],
    // Sprint 280: claves de economía de tokens con representación en
    // hive/agente (el resto — medición DR/tenant, presupuesto — no viaja aquí).
    [
      'optimization',
      compact({
        enabled: fields.optimization_enabled,
        measurement_enabled: fields.measurement_enabled,
        compare_tolerance_pct: fields.compare_tolerance_pct,
        holdout_pct: fields.optimization_holdout_pct,
        tool_exposure: fields.tool_exposure,
        tool_deferred_threshold_tokens: fields.tool_deferred_threshold_tokens,
        compaction_trigger_pct: fields.compaction_trigger_pct,
        compaction_target_pct: fields.compaction_target_pct,
        compaction_on_cold_cache: fields.compaction_on_cold_cache,
      }),
    ],
    [
      'cache',
      compact({
        enabled: fields.prompt_cache_enabled,
        ttl: fields.prompt_cache_ttl,
        partition: fields.prompt_cache_partition,
      }),
    ],
    [
      'execution',
      compact({
        output_effort: fields.output_effort,
        output_effort_dynamic: fields.output_effort_dynamic,
        async_service_tier: fields.async_service_tier,
      }),
    ],
    ['loop', compact({ max_iterations: fields.max_loop_iterations })],
    [
      'autonomy',
      compact({
        frontier: fields.frontier_agents_enabled,
        max_duration_minutes: fields.frontier_max_duration_minutes,
        on_unavailable: fields.frontier_on_unavailable,
      }),
    ],
    [
      'timeouts',
      compact({
        foreground_seconds: fields.foreground_timeout_seconds,
        delegation_sync_seconds: fields.delegation_sync_timeout_seconds,
        delegation_background_seconds:
          fields.delegation_background_timeout_seconds,
        fanout_aggregation_seconds:
          fields.fanout_aggregation_timeout_seconds,
      }),
    ],
  ];
  for (const [key, block] of blocks) {
    if (block) next[key] = block;
    else delete next[key];
  }
  return next;
}
